import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, MapPin, Tag, Calendar, Bookmark, BookmarkCheck, ExternalLink } from 'lucide-react';

type LibraryItem = {
  id: string; title: string; sourceUrl: string; llmReadyContent: string;
  summary?: string; relevanceReason?: string; province: string | null;
  city: string | null; dateCrawled: string; tags: string[]; bookmarked: boolean; similarity?: number;
};
type TagObj = { id: string; name: string };

export default function MyLibrary() {
  const [items, setItems]     = useState<LibraryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [cities, setCities]   = useState<string[]>([]);
  const [provinces, setProvinces] = useState<string[]>([]);
  const [tags, setTags]       = useState<TagObj[]>([]);
  const [filters, setFilters] = useState({ tag:'', province:'', city:'', start:'', end:'', search:'', bookmarked: false });

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k,v]) => { if (v !== '' && v !== false) params.append(k, String(v)); });
    try { const r = await axios.get(`/api/library?${params}`); setItems(r.data); }
    catch(e) { console.error(e); } finally { setLoading(false); }
  };

  const smartSearch = async () => {
    if (!search.trim()) return load();
    setLoading(true);
    try { const r = await axios.post('/api/library/search', { query: search }); setItems(r.data); }
    catch(e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    axios.get('/api/library/cities').then(r => setCities(r.data)).catch(console.error);
    axios.get('/api/library/provinces').then(r => setProvinces(r.data)).catch(console.error);
    axios.get('/api/library/tags').then(r => setTags(r.data)).catch(console.error);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleBookmark = (id: string, cur: boolean) =>
    axios.put(`/api/library/${id}/bookmark`, { bookmarked: !cur }).then(() =>
      setItems(p => p.map(i => i.id===id ? { ...i, bookmarked: !cur } : i))
    ).catch(console.error);

  const set = (k: string, v: string | boolean) => setFilters(p => ({ ...p, [k]: v }));

  return (
    <div className="bg-transparent min-h-full p-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold text-[color:var(--text-primary)] m-0 mb-1">Library</h1>
        <p className="text-[13px] text-[color:var(--text-muted)] m-0">Search and browse your curated knowledge base</p>
      </div>

      <div className="bg-[var(--glass-bg)] backdrop-blur-[12px] border border-[var(--glass-border)] rounded-[16px] p-5 mb-6 flex flex-col gap-[14px]">
        {/* Row 1 */}
        <div className="flex gap-[10px] flex-wrap items-center">
          <div className="relative flex-[2] min-w-[200px]">
            <Search size={14} color="var(--text-muted)" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Semantic search…"
              className="flex-1 min-w-[160px] w-full bg-[var(--surface)] border border-[var(--glass-border)] rounded-[10px] pl-[34px] px-3 py-2 text-[13px] text-[color:var(--text-primary)] outline-none"
              onKeyDown={e => e.key==='Enter' && smartSearch()}
            />
          </div>
          <select
            value={filters.tag||'all'}
            onChange={e => set('tag', e.target.value==='all'?'':e.target.value)}
            className="flex-1 min-w-[140px] appearance-none bg-[var(--surface)] border border-[var(--glass-border)] rounded-[10px] px-3 py-2 text-[13px] text-[color:var(--text-secondary)] outline-none cursor-pointer"
          >
            <option value="all">All Tags</option>
            {tags.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
          <select
            value={filters.province||'all'}
            onChange={e => set('province', e.target.value==='all'?'':e.target.value)}
            className="flex-1 min-w-[140px] appearance-none bg-[var(--surface)] border border-[var(--glass-border)] rounded-[10px] px-3 py-2 text-[13px] text-[color:var(--text-secondary)] outline-none cursor-pointer"
          >
            <option value="all">All Provinces</option>
            {provinces.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select
            value={filters.city||'all'}
            onChange={e => set('city', e.target.value==='all'?'':e.target.value)}
            className="flex-1 min-w-[140px] appearance-none bg-[var(--surface)] border border-[var(--glass-border)] rounded-[10px] px-3 py-2 text-[13px] text-[color:var(--text-secondary)] outline-none cursor-pointer"
          >
            <option value="all">All Cities</option>
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {/* Row 2 */}
        <div className="flex gap-[10px] flex-wrap items-center">
          <input
            type="date"
            value={filters.start}
            onChange={e => set('start',e.target.value)}
            className="bg-[var(--surface)] border border-[var(--border)] rounded-[10px] px-3 py-2 text-[13px] text-[color:var(--text-secondary)] outline-none"
          />
          <span className="text-[color:var(--border-subtle)] text-[13px]">to</span>
          <input
            type="date"
            value={filters.end}
            onChange={e => set('end',e.target.value)}
            className="bg-[var(--surface)] border border-[var(--border)] rounded-[10px] px-3 py-2 text-[13px] text-[color:var(--text-secondary)] outline-none"
          />
          <label className="flex items-center gap-2 text-[13px] text-[color:var(--text-secondary)] cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={filters.bookmarked}
              onChange={e => set('bookmarked',e.target.checked)}
              style={{ accentColor: 'var(--accent)', width:'15px', height:'15px' }}
            />
            Bookmarked only
          </label>
          <div className="flex-1" />
          <button
            onClick={smartSearch}
            className="px-5 py-2 bg-[var(--accent)] border-none rounded-[10px] text-[13px] font-semibold text-white cursor-pointer whitespace-nowrap transition-colors hover:bg-[var(--accent-strong)]"
          >Search</button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-[80px] gap-2 items-center">
          {[0,150,300].map(d => (
            <div
              key={d}
              className="w-2 h-2 rounded-full bg-[var(--accent)]"
              style={{ animation:`bounce 1s ${d}ms infinite` }}
            />
          ))}
        </div>
      )}
      {!loading && items.length===0 && (
        <div className="text-center py-[80px] text-[color:var(--border-subtle)] text-[14px]">
          No results found. Try adjusting your filters.
        </div>
      )}

      {!loading && (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(460px,1fr))] gap-4">
          {items.map(item => (
            <div
              key={item.id}
              className="bg-[var(--glass-bg)] backdrop-blur-[8px] border border-[var(--glass-border)] rounded-[16px] p-[22px] flex flex-col gap-3 transition-colors hover:border-[var(--border-subtle)]"
            >
              <div className="flex justify-between items-start gap-[10px]">
                <h3 className="text-[15px] font-semibold text-[color:var(--text-primary)] leading-[1.4] m-0">{item.title}</h3>
                <button
                  className="bg-transparent border-none cursor-pointer p-[2px] shrink-0 transition-colors"
                  style={{ color: item.bookmarked ? '#f59e0b' : 'var(--text-muted)' }}
                  onClick={() => toggleBookmark(item.id, item.bookmarked)}
                  title={item.bookmarked ? 'Remove bookmark' : 'Bookmark'}
                >
                  {item.bookmarked ? <BookmarkCheck size={17} /> : <Bookmark size={17} />}
                </button>
              </div>
              {item.summary && <p className="text-[13px] text-[color:var(--text-muted)] leading-[1.6] m-0">{item.summary}</p>}
              <div className="flex gap-2 flex-wrap items-center">
                {item.tags.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 px-[10px] py-[3px] bg-[var(--surface)] border border-[var(--border)] rounded-[20px] text-[11px] text-[color:var(--text-secondary)]">
                    <Tag size={10}/>{t}
                  </span>
                ))}
                {(item.city||item.province) && (
                  <span className="inline-flex items-center gap-1 px-[10px] py-[3px] bg-[rgba(59,130,246,0.08)] border border-[rgba(59,130,246,0.2)] rounded-[20px] text-[11px] text-[color:var(--accent-blue)]">
                    <MapPin size={10}/>
                    {item.city&&item.province ? `${item.city}, ${item.province}` : item.city||item.province}
                  </span>
                )}
                {item.dateCrawled && (
                  <span className="inline-flex items-center gap-1 px-[10px] py-[3px] bg-[var(--surface)] border border-[var(--border)] rounded-[20px] text-[11px] text-[color:var(--text-secondary)]">
                    <Calendar size={10}/>{new Date(item.dateCrawled).toLocaleDateString()}
                  </span>
                )}
                {item.similarity !== undefined && (
                  <span className="inline-flex items-center gap-1 px-2 py-[2px] bg-[var(--accent-dim)] border border-[var(--accent-border)] rounded-[20px] text-[11px] text-[color:var(--accent-strong)]">
                    {Math.round(item.similarity*100)}% match
                  </span>
                )}
              </div>
              <div className="h-px bg-[var(--border)]" />
              <div>
                <a
                  href={item.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-[6px] px-[14px] py-[7px] bg-[rgba(59,130,246,0.08)] border border-[rgba(59,130,246,0.2)] rounded-[10px] text-[12px] font-medium text-[color:var(--accent-blue)] no-underline transition-all hover:bg-[rgba(59,130,246,0.15)]"
                >
                  <ExternalLink size={13}/> View Source
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
      <style>{`@keyframes bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-7px)} }`}</style>
    </div>
  );
}
